/* eslint-disable react/no-danger */
/* eslint-disable camelcase */
/* eslint-disable no-shadow */
import React, { createRef, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Loader,
  SectionHeader,
  ActionIcon,
  ToggleSwitch,
  PrintProgressLoader,
  usePrintDispatch,
} from '@mummssoftware/common-ui';
import { useIntl } from 'react-intl';
import { Contents, FAJITA } from '@mummssoftware/utils/constants';
import Print from '@material-ui/icons/Print';
import Check from '@material-ui/icons/Check';
import { LinearProgress, Menu, MenuItem } from '@material-ui/core';
import { PrintProgress } from '@mummssoftware/utils/api/Printing';
import { Printing } from '@mummssoftware/utils';
import { createPortal } from 'react-dom';
import { FajitaForms } from './FajitaForms';
import { ManifestContext, questionnaires } from '../../context';
import { Container, ContainerStyles } from './Container';
import { unify } from './UnifyForm';
import { staticIdgPrintJquery } from '../utils/staticIdgPrintJquery';

import 'ag-grid-community/dist/styles/agGridMaterialFont.css';
import 'ag-grid-community/dist/styles/ag-grid.css';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';
import defaults from '../../translations/en.json';
import '../utils/styles.css';

const Blank = styled.div`
  display: none;
`;

const ModalBackground = styled.div`
  display: block !important;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: scroll;
  z-index: 10000;
  background-color: rgba(0, 0, 0, 0.5);
`;

const ToggleDiv = styled.div`
  display: inline-block;
  margin: 15px 3px 0 0;
  color: ${(props) => props.theme.palette.text.primary};
`;

const LinearProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1rem;
  @media print {
    display: none;
  }
`;

const LinearProgressDiv = styled.div`
  width: 90%;
`;

export type PrintModalProps = {
  display: boolean;
  selectedQs?: questionnaires;
  selectedForms?: mumms.GenericForm[];
  clearModal: () => void;
  patient?: mumms.patient;
  facesheet?: string;
  printConfig?: mumms.printConfig;
  selectedCount: number;
  printedCount: number;
  isOverMaxPrintCount?: boolean;
  onFinishedPrinting?: (finishedPrinting: boolean) => void | undefined;
};

export const unifyForm = unify;

export const PrintModal = ({
  display,
  selectedQs,
  selectedForms,
  clearModal,
  patient,
  facesheet,
  printConfig,
  selectedCount,
  printedCount,
  onFinishedPrinting,
}: PrintModalProps) => {
  const selectedQEs = selectedQs?.filter((q) => q?.form_tech_type !== FAJITA);
  const selectedFAJITAs = selectedQs?.filter(
    (q) => q?.form_tech_type === FAJITA,
  );
  const hasFajita = selectedFAJITAs && !!selectedFAJITAs?.length;
  const qHtml = `<br/>${selectedQEs
    ?.map((q) => q?.html)
    .join('<div style="background-color: #454545;"><br/></div>')}`;
  /** IDG Review Form Layout */
  const modifiedqHtml = qHtml
    .replaceAll('<br/>"', '<br/>')
    .replaceAll('</div>"', '</div>');

  const [faceDisplayed, changeFaceDisplay] = useState<boolean>(false);
  const displayFace = () => {
    changeFaceDisplay(!faceDisplayed);
  };
  const contentRef = createRef<HTMLDivElement>();

  const html = faceDisplayed
    ? `<br/>${facesheet}${modifiedqHtml}`
    : modifiedqHtml;
  let styles = ContainerStyles;
  styles = styles.replace('width: 80%', 'width: 100%');
  styles = styles.replace(/background-color: [^;]+;/g, '');
  styles = styles.replace(/color: [^;]+;/g, '');

  const { keycloak, currentUser, agency } = useContext(ManifestContext);
  const { formatMessage } = useIntl();

  const [loading, setLoading] = useState(false);
  const [printProgress, setPrintProgress] = useState<any>(null);
  const [printMenuAnchor, setPrintMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const hasPrintMenu = true;

  const [progress, setProgress] = useState<number>(0);

  const printingService = React.useMemo(
    () =>
      new Printing({
        url: window.env.hosts.printingService as string,
        keycloak,
        agency: agency || '',
        currentUser,
      }),
    [keycloak, agency, currentUser],
  );

  const getPrintPromise = React.useCallback(
    (faxInstructions: any, emailInstructions: any) =>
      printingService.printManifest(
        selectedForms as any,
        patient!,
        faceDisplayed,
        (p: PrintProgress) => setPrintProgress(p),
        faxInstructions,
        emailInstructions,
      ),
    [printingService, selectedForms, patient, faceDisplayed],
  );

  const { print, fax, email } = usePrintDispatch(
    getPrintPromise,
    () => setLoading(true),
    () => {
      setLoading(false);
      setPrintProgress(null);
    },
  );

  const onPrint = (wantFax: boolean) => {
    if (!selectedForms || !patient) return;
    if (wantFax) fax();
    else print();
  };
  const onEmail = () => {
    if (!selectedForms || !patient) return;
    email();
  };

  useEffect(() => {
    const updatedProgress = (printedCount / selectedCount) * 100;
    setProgress(updatedProgress);
    if (updatedProgress === 100) {
      onFinishedPrinting && onFinishedPrinting(true);
    }
  }, [printedCount, selectedCount, onFinishedPrinting]);

  // do not add a dependency to this useEffect.
  // This is needed in order to ensure the accordion styling and behavior stays within the idg document
  // when printing from The Manifest, and stays as long as this component is rendered.
  useEffect(() => {
    staticIdgPrintJquery();
  });

  const LinearProgressWithLabel = () => (
    <LinearProgressContainer>
      <LinearProgressDiv>
        <LinearProgress variant="determinate" value={progress} />
      </LinearProgressDiv>
      <div>{`${printedCount}/${selectedCount}`}</div>
    </LinearProgressContainer>
  );

  if (!display) {
    return <Blank />;
  }

  if (!html) {
    return (
      <ModalBackground>
        <Loader />
      </ModalBackground>
    );
  }

  if (printConfig?.view === Contents) {
    return (
      <div style={{ height: '100%', width: '100%' }}>
        {LinearProgressWithLabel()}
        <div id="print-manifest-content" ref={contentRef}>
          <div
            id={`${
              selectedCount > 1 ? 'multiple-selected' : 'single-selected'
            }`}
            dangerouslySetInnerHTML={{
              __html: html,
            }}
          />
          {hasFajita && <FajitaForms forms={selectedFAJITAs as any} />}
        </div>
      </div>
    );
  }

  return (
    <ModalBackground>
      {loading &&
        createPortal(
          <PrintProgressLoader
            progress={printProgress}
            sourceString="The Manifest"
          />,
          document.getElementById('portal-container') || document.body,
        )}
      <Container id="print-modal">
        <SectionHeader
          action={
            <div className="MuiButtonBase-root">
              <ActionIcon
                title={hasPrintMenu ? undefined : 'Print'}
                Icon={Print}
                onClick={(e) =>
                  hasPrintMenu
                    ? setPrintMenuAnchor(e.currentTarget)
                    : onPrint(false)
                }
              />
              {hasPrintMenu && (
                <Menu
                  anchorEl={printMenuAnchor}
                  open={Boolean(printMenuAnchor)}
                  onClose={() => setPrintMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                  <MenuItem
                    onClick={() => {
                      setPrintMenuAnchor(null);
                      onPrint(false);
                    }}
                  >
                    {formatMessage({
                      id: 'widget.header.print',
                      defaultMessage: 'Print',
                    })}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setPrintMenuAnchor(null);
                      onPrint(true);
                    }}
                  >
                    {formatMessage({
                      id: 'widget.header.fax',
                      defaultMessage: 'Fax',
                    })}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setPrintMenuAnchor(null);
                      onEmail();
                    }}
                  >
                    Email
                  </MenuItem>
                </Menu>
              )}
              {patient && (
                <ToggleDiv>
                  <ToggleSwitch
                    rightText={defaults.displayFacesheet}
                    leftText=""
                    isChecked={faceDisplayed}
                    onToggle={displayFace}
                  />
                </ToggleDiv>
              )}
            </div>
          }
          nav={<ActionIcon title="" Icon={Check} onClick={clearModal} />}
          title=""
        />
        {LinearProgressWithLabel()}
        <div id="print-manifest-content" ref={contentRef}>
          <div
            id={`${
              selectedCount > 1 ? 'multiple-selected' : 'single-selected'
            }`}
            dangerouslySetInnerHTML={{
              __html: html,
            }}
          />
          {hasFajita && <FajitaForms forms={selectedFAJITAs as any} />}
        </div>
      </Container>
    </ModalBackground>
  );
};
